import * as React from 'react';
import { Loader } from '@grandvisionhq/common';
import { Container, Heading } from '@grandvisionhq/elements';
import { actions as actions$1, reducer, useIntl } from '@grandvisionhq/state';
import * as swcHelpers from '@swc/helpers';
import { createScriptTag, injectTags } from '@grandvisionhq/utils';

const loadReviewWrite = actions$1.action('LOAD_REVIEW_WRITE', actions$1.payload());
const loadReviewSnippet = actions$1.action('LOAD_REVIEW_SNIPPET', actions$1.payload());
const loadReviewDisplay = actions$1.action('LOAD_REVIEW_DISPLAY', actions$1.payload());
const updateReviewDisplay = actions$1.action('UPDATE_REVIEW_DISPLAY', actions$1.payload());

var actions = /*#__PURE__*/Object.freeze({
  __proto__: null,
  loadReviewWrite: loadReviewWrite,
  loadReviewSnippet: loadReviewSnippet,
  loadReviewDisplay: loadReviewDisplay,
  updateReviewDisplay: updateReviewDisplay
});

/**
 * Maps our settings to an object that's accepted by PowerReviews.display.render() method
 */ const createPowerReviewsConfig = ({ config , locale , productId , variantId , type , id , returnUrl , onRender , onSubmit  })=>swcHelpers.objectSpread({
        enable_content_collection_modal: false,
        api_key: config.apiKey,
        locale: locale.replace('-', '_'),
        merchant_group_id: config.groupId,
        merchant_id: config.opcoId,
        // Append the "?" as PowerReviews doesn't do that but will add query params.
        review_wrapper_url: `${config.writeReviewPath}?`,
        components: {
            [type]: id
        }
    }, productId && {
        page_id: productId
    }, variantId && {
        page_id_variant: variantId
    }, returnUrl && {
        return_url: returnUrl
    }, onRender && {
        on_render: onRender
    }, onSubmit && {
        on_submit: onSubmit
    })
;
/**
 * PowerReviews doesn't accept multiple widgets to be loaded separately.
 * It always expects one object instead of an array or multiple calls to the render fn.
 */ const mergeConfigs = (configs)=>{
    return configs.reduce((result, next)=>swcHelpers.objectSpread({}, result, next, {
            components: swcHelpers.objectSpread({}, result.components, next.components)
        })
    , {});
};

const initialState = {
    reviewWrite: null,
    reviewSnippet: null,
    reviewDisplay: null
};
const reviewsReducer = reducer(initialState, /**
   * Load widgets
   */ actions$1.on(loadReviewWrite, (state, action)=>swcHelpers.objectSpread({}, state, {
        reviewWrite: action.payload
    })
), actions$1.on(loadReviewSnippet, (state, action)=>swcHelpers.objectSpread({}, state, {
        reviewSnippet: action.payload
    })
), actions$1.on(loadReviewDisplay, (state, action)=>swcHelpers.objectSpread({}, state, {
        reviewDisplay: action.payload
    })
), /**
   * Update widgets
   */ actions$1.on(updateReviewDisplay, (state, action)=>{
    return state.reviewDisplay !== null ? swcHelpers.objectSpread({}, state, {
        reviewDisplay: swcHelpers.objectSpread({}, state.reviewDisplay, {
            variantId: action.payload.variantId
        })
    }) : state;
}));

// These are the keys PowerReviews requires in the `components` object when initializing a widget
const mapStateToPowerReviewsType = {
    reviewWrite: 'Write',
    reviewSnippet: 'ReviewSnippet',
    reviewDisplay: 'ReviewDisplay'
};
const ReviewsContext = /*#__PURE__*/ React.createContext({});
const useReviewsContext = ()=>React.useContext(ReviewsContext)
;
const PR_SCRIPT_ID = 'power-reviews-script';
const PR_SCRIPT_SRC = 'https://ui.powerreviews.com/stable/4.1/ui.js';
const ReviewsProvider = ({ enabled , config , children  })=>{
    const [state, dispatch] = React.useReducer(reviewsReducer, initialState);
    const { locale  } = useIntl();
    const widgets = React.useMemo(()=>{
        return Object.keys(state).reduce((result, key)=>{
            if (state[key] !== null) {
                const widgetConfig = createPowerReviewsConfig(swcHelpers.objectSpread({
                    config,
                    locale
                }, state[key], {
                    type: mapStateToPowerReviewsType[key]
                }));
                return [
                    ...result,
                    widgetConfig
                ];
            }
            return result;
        }, []);
    }, [
        state
    ]);
    React.useEffect(()=>{
        const script = createScriptTag({
            id: PR_SCRIPT_ID,
            src: PR_SCRIPT_SRC,
            defer: true
        });
        injectTags([
            script
        ]);
    }, []);
    React.useEffect(()=>{
        if (enabled && widgets.length) {
            const mergedConfig = widgets.length > 1 ? mergeConfigs(widgets) : widgets[0];
            // Clear existing widgets and loaders before (re)initializing
            Object.values(mergedConfig.components).forEach((widgetId)=>{
                clearWidgetHtml(widgetId);
            });
            // Set powerreviews configuration. powerreviews has a complex async script loading and does not provide a proper load hook.
            // To prevent a race condition we prepare the config beforehand.
            // This is the official way powerreviews should be declared.
            window.pwr = window.pwr || function(...args) {
                (pwr.q = pwr.q || []).push(args);
            };
            window.pwr('render', mergedConfig);
            // When new widgets are updated, first remove the old from powerreviews as they might have the same id and therefore will not be rendered.
            return ()=>{
                Object.values(mergedConfig.components).forEach((widgetId)=>{
                    window.pwr('unmount', widgetId);
                });
            };
        }
    }, [
        widgets,
        enabled
    ]);
    const value = React.useMemo(()=>({
            enabled,
            config,
            dispatch
        })
    , [
        enabled,
        config
    ]);
    return /*#__PURE__*/ React.createElement(ReviewsContext.Provider, {
        value: value
    }, children);
};
// Because PowerReviews doesn't clear their widget's HTML themselves
// when we re-call their `display` method
function clearWidgetHtml(id) {
    const element = document.getElementById(id);
    if (element) {
        element.innerHTML = '';
    }
}

const REVIEW_DISPLAY_ID = 'pr-review-display';
const ReviewDisplay = ({ productId , variantId  })=>{
    const { enabled , dispatch  } = useReviewsContext();
    React.useEffect(()=>{
        const observer = new MutationObserver(()=>{
            if (document.querySelector('#pr-review-snapshot')) {
                const mainDiv = document.querySelector(`#${REVIEW_DISPLAY_ID}`);
                const svgIcons = mainDiv && [
                    ...mainDiv.getElementsByTagName('svg')
                ];
                svgIcons === null || svgIcons === void 0 ? void 0 : svgIcons.forEach((svg)=>{
                    svg.setAttribute('aria-hidden', 'true');
                });
                observer.disconnect();
            }
        });
        observer.observe(document.body, {
            childList: true,
            subtree: true
        });
    }, []);
    React.useEffect(()=>{
        if (enabled && productId) {
            dispatch(loadReviewDisplay({
                id: REVIEW_DISPLAY_ID,
                productId,
                variantId
            }));
        }
    }, [
        dispatch,
        enabled,
        productId,
        variantId
    ]);
    if (!enabled) {
        return null;
    }
    return /*#__PURE__*/ React.createElement("div", {
        className: "review-display",
        "data-t": "review-display"
    }, /*#__PURE__*/ React.createElement(Container, null, /*#__PURE__*/ React.createElement("div", {
        id: REVIEW_DISPLAY_ID
    }, /*#__PURE__*/ React.createElement(Loader, null))));
};

const REVIEW_SNIPPET_ID = 'pr-review-snippet';
const ReviewSnippet = ({ productId , variantId  })=>{
    const { enabled , dispatch  } = useReviewsContext();
    React.useEffect(()=>{
        if (enabled && productId) {
            dispatch(loadReviewSnippet({
                id: REVIEW_SNIPPET_ID,
                productId,
                variantId
            }));
        }
    }, [
        enabled,
        productId,
        variantId
    ]);
    if (!enabled) {
        return null;
    }
    return /*#__PURE__*/ React.createElement("div", {
        className: "review-snippet",
        "data-t": "review-snippet"
    }, /*#__PURE__*/ React.createElement("div", {
        id: REVIEW_SNIPPET_ID
    }));
};

const REVIEW_WRITE_ID = 'pr-review-write';
const ReviewWrite = ({ selfUrl  })=>{
    const { getLabel  } = useIntl();
    const { enabled , dispatch  } = useReviewsContext();
    const [submitted, setSubmitted] = React.useState(false);
    const onSubmit = React.useCallback(()=>{
        setSubmitted(true);
        window && window.scrollTo(0, 0);
    }, []);
    React.useEffect(()=>{
        if (enabled) {
            dispatch(loadReviewWrite({
                id: REVIEW_WRITE_ID,
                returnUrl: selfUrl,
                onSubmit
            }));
        }
    }, [
        enabled,
        selfUrl,
        onSubmit
    ]);
    if (!enabled) {
        return null;
    }
    return /*#__PURE__*/ React.createElement("div", {
        className: "review-write",
        "data-t": "review-write"
    }, /*#__PURE__*/ React.createElement(Container, null, !submitted && /*#__PURE__*/ React.createElement(Heading, {
        variant: "small",
        component: "h1"
    }, getLabel('reviews.write.title')), /*#__PURE__*/ React.createElement("div", {
        id: REVIEW_WRITE_ID
    })));
};

export { actions as Actions, PR_SCRIPT_ID, PR_SCRIPT_SRC, ReviewDisplay, ReviewSnippet, ReviewWrite, ReviewsProvider, mapStateToPowerReviewsType, useReviewsContext };
//# sourceMappingURL=index.js.map
